/**
 * Review drawer — shows conversation-level approval requests and lets
 * an approver submit a review (approve / reject / change state).
 * Mirrors KC2's ReviewForm: state dropdown + remark.
 */
import { useEffect, useState, type JSX } from 'react'
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowRight,
  FileText,
  MessageSquare,
} from 'lucide-react'
import { api } from '../../../api/client.js'
import { useToast } from '../../../components/system/ToastProvider.js'
import { useAuthStore } from '../../../store/authStore.js'
import { useApprovalCountsStore } from '../../../store/approvalCountsStore.js'
import { formatDateTime } from '../../../utils/timezone.js'
import ActionDrawer from './ActionDrawer.js'
import { Field, GhostButton, PrimaryButton, SelectField, getAvatarColor, getInitials, textareaClass } from './primitives.js'

interface ApprovalState {
  id: string
  name: string
  description: string | null
  colorCode: string | null
}

interface ApprovalRequest {
  id: string
  conversationId: string
  senderId: string
  senderName: string | null
  approverType: number
  approverId: string | null
  approverName: string | null
  stateId: string | null
  stateName: string | null
  levelName: string | null
  senderRemark: string | null
  reviewed: boolean
  dateReviewed: string | null
  reviewRemark: string | null
  dateSent: string
  createdAt: string
}

export interface ReviewDrawerProps {
  open: boolean
  onClose: () => void
  conversationId: string
  currentAgentId: string | null
  onReviewed: () => void
}

function PersonChip({ name }: { name: string }): JSX.Element {
  return (
    <span
      className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold ${getAvatarColor(name)}`}
    >
      {getInitials(name)}
    </span>
  )
}

function MetaItem({
  label,
  value,
  avatar,
}: {
  label: string
  value: string | null
  avatar?: boolean
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <div className="flex items-center gap-2">
        {avatar && value && <PersonChip name={value} />}
        <span className="truncate text-[13px] font-medium text-slate-900">{value ?? '—'}</span>
      </div>
    </div>
  )
}

function RemarkBlock({
  icon,
  label,
  text,
}: {
  icon: JSX.Element
  label: string
  text: string
}): JSX.Element {
  return (
    <div className="rounded-[3px] border border-slate-100 border-l-4 border-l-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        {icon}
        {label}
      </div>
      <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-slate-700">{text}</p>
    </div>
  )
}

function StatePill({
  name,
  colorCode,
}: {
  name: string | null
  colorCode?: string | null
}): JSX.Element {
  const label = name ?? 'Reviewed'
  const dot = <span className="h-1.5 w-1.5 rounded-full bg-current" />
  if (colorCode) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-medium"
        style={{ color: colorCode, backgroundColor: `${colorCode}15`, borderColor: `${colorCode}40` }}
      >
        {dot}
        {label}
      </span>
    )
  }
  const lower = label.toLowerCase()
  if (lower.includes('reject') || lower.includes('decline')) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-[11px] font-medium text-red-700">
        {dot}
        {label}
      </span>
    )
  }
  if (lower.includes('approve') || lower.includes('accept')) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-medium text-emerald-700">
        {dot}
        {label}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-medium text-slate-600">
      {dot}
      {label}
    </span>
  )
}

export default function ReviewDrawer({
  open,
  onClose,
  conversationId,
  currentAgentId,
  onReviewed,
}: ReviewDrawerProps): JSX.Element {
  const toast = useToast()
  const canApprove = useAuthStore((s) => s.hasPermission('conversations:approve'))
  const [requests, setRequests] = useState<ApprovalRequest[]>([])
  const [states, setStates] = useState<ApprovalState[]>([])
  const [loading, setLoading] = useState(false)
  const [reviewingId, setReviewingId] = useState<string | null>(null)
  const [reviewStateId, setReviewStateId] = useState('')
  const [remark, setRemark] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    Promise.all([
      api.get<{ requests: ApprovalRequest[] }>(`/conversations/${conversationId}/approval-requests`),
      api.get<{ states: ApprovalState[] }>('/conversations/approval-states'),
    ])
      .then(([reqRes, statesRes]) => {
        if (cancelled) return
        setRequests(reqRes.requests ?? [])
        setStates(statesRes.states ?? [])
      })
      .catch(() => {
        if (!cancelled) {
          setRequests([])
          setStates([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, conversationId])

  const startReview = (reqId: string): void => {
    setReviewingId(reqId)
    setReviewStateId('')
    setRemark('')
  }

  const cancelReview = (): void => {
    setReviewingId(null)
    setReviewStateId('')
    setRemark('')
  }

  const submitReview = async (): Promise<void> => {
    if (!reviewingId || !reviewStateId) return
    setBusy(true)
    try {
      await api.post(`/conversations/${conversationId}/submit-review`, {
        approvalRequestId: reviewingId,
        stateId: reviewStateId,
        ...(remark.trim() ? { remark: remark.trim() } : {}),
      })
      toast.success('Review submitted successfully')
      const res = await api.get<{ requests: ApprovalRequest[] }>(
        `/conversations/${conversationId}/approval-requests`,
      )
      const updated = res.requests ?? []
      setRequests(updated)
      // Refresh the header approval badge so approved/rejected/pending counts
      // update immediately after this decision, not on the next dropdown open.
      void useApprovalCountsStore.getState().refresh()
      onReviewed()
      cancelReview()
      // Close the drawer once nothing is left to review — the reviewer is done.
      // If other requests are still pending, keep it open to review them.
      if (updated.every((r) => r.reviewed)) onClose()
    } catch (e) {
      toast.error('Review failed', e instanceof Error ? e.message : 'Unexpected error')
    } finally {
      setBusy(false)
    }
  }

  const pendingRequests = requests.filter((r) => !r.reviewed)
  const reviewedRequests = requests.filter((r) => r.reviewed)

  return (
    <ActionDrawer
      open={open}
      onClose={onClose}
      title="Approval Requests"
      subtitle="Review and approve pending approval requests"
      icon={<ShieldCheck className="h-4 w-4" />}
      info={{
        lead: 'Sign-off requests raised on this conversation, for the approvers named on each one.',
        points: [
          { term: 'State', detail: 'The outcome you are recording — the states available come from your tenant\u2019s approval configuration.' },
          { term: 'Remark', detail: 'Your reasoning. It is kept with the request and visible to the requesting agent.' },
          { term: 'While pending', detail: 'A conversation awaiting approval cannot be merged into another one.' },
        ],
      }}
      width={1020}
    >
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-[#3366FF]" />
          <p className="mt-4 text-sm text-slate-500">Loading approval requests…</p>
        </div>
      ) : requests.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24">
          <div className="flex h-16 w-16 items-center justify-center rounded-[3px] bg-[#F5F7FF]">
            <ShieldCheck className="h-8 w-8 text-[#3366FF]/30" strokeWidth={1.5} />
          </div>
          <p className="mt-4 text-sm font-semibold text-slate-700">No approval requests</p>
          <p className="mt-1 text-xs text-slate-500">Approval requests will appear here when sent</p>
        </div>
      ) : (
        <div className="mx-auto max-w-4xl space-y-10">
          {/* ── Pending Section ── */}
          {pendingRequests.length > 0 && (
            <section>
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100">
                  <Clock className="h-4 w-4 text-amber-600" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[13px] font-bold text-amber-700">Pending</span>
                  <span className="text-[11px] text-slate-400">{pendingRequests.length} awaiting review</span>
                </div>
                <div className="h-px flex-1 bg-slate-200" />
              </div>

              <div className="space-y-4">
                {pendingRequests.map((req) => (
                  <div
                    key={req.id}
                    className="overflow-hidden rounded-[3px] border border-slate-200 bg-white shadow-sm"
                  >
                    <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/60 px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 rounded-[3px] border border-amber-100 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
                        <Clock className="h-3 w-3" />
                        Pending review
                      </span>
                      <span className="flex items-center gap-1.5 text-[11px] text-slate-400">
                        <Clock className="h-3 w-3" />
                        Sent {formatDateTime(req.dateSent)}
                      </span>
                    </div>

                    <div className="p-4">
                      <div className="mb-4 grid grid-cols-2 gap-x-6 gap-y-4">
                        <MetaItem label="Sender" value={req.senderName} avatar />
                        <MetaItem label="Approver" value={req.approverName} avatar />
                        <MetaItem label="Level" value={req.levelName} />
                        <MetaItem label="State" value={req.stateName} />
                      </div>

                      {req.senderRemark && (
                        <RemarkBlock icon={<MessageSquare className="h-3 w-3" />} label="Sender Remark" text={req.senderRemark} />
                      )}

                      {reviewingId === req.id ? (
                        <div className="mt-4 rounded-[3px] border border-[#3366FF]/15 bg-[#F8FAFF] p-4">
                          <div className="mb-3 flex items-center gap-2 text-[12px] font-bold text-[#3366FF]">
                            <ArrowRight className="h-3.5 w-3.5" />
                            Submit Review
                          </div>
                          <div className="space-y-3">
                            <Field label="Review State" required>
                              <SelectField
                                value={reviewStateId}
                                onChange={setReviewStateId}
                                placeholder="Select review state"
                                options={states
                                  .filter((s) => s.id !== req.stateId)
                                  .map((s) => ({ value: s.id, label: s.name }))}
                              />
                            </Field>
                            <Field label="Remark">
                              <textarea
                                value={remark}
                                onChange={(e) => setRemark(e.target.value)}
                                rows={3}
                                placeholder="Add a review remark (optional)…"
                                className={textareaClass}
                              />
                            </Field>
                            <div className="flex justify-end gap-2 pt-1">
                              <GhostButton onClick={cancelReview} disabled={busy}>
                                Cancel
                              </GhostButton>
                              <PrimaryButton
                                onClick={() => void submitReview()}
                                disabled={!reviewStateId || busy}
                              >
                                {busy ? 'Submitting…' : 'Submit Review'}
                              </PrimaryButton>
                            </div>
                          </div>
                        </div>
                      ) : canApprove && req.approverId === currentAgentId ? (
                        <div className="mt-4 flex justify-end">
                          <PrimaryButton onClick={() => startReview(req.id)}>
                            <ShieldCheck className="mr-1.5 h-3.5 w-3.5" />
                            Review
                          </PrimaryButton>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ── Reviewed Section ── */}
          {reviewedRequests.length > 0 && (
            <section>
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[13px] font-bold text-slate-700">Reviewed</span>
                  <span className="text-[11px] text-slate-400">{reviewedRequests.length} completed</span>
                </div>
                <div className="h-px flex-1 bg-slate-200" />
              </div>

              <div className="space-y-4">
                {reviewedRequests.map((req) => {
                  const state = states.find((s) => s.id === req.stateId)
                  return (
                    <div
                      key={req.id}
                      className="overflow-hidden rounded-[3px] border border-slate-200 bg-white shadow-sm"
                    >
                      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/60 px-4 py-3">
                        <StatePill name={req.stateName} colorCode={state?.colorCode ?? null} />
                        <span className="flex items-center gap-1.5 text-[11px] text-slate-400">
                          {req.stateName?.toLowerCase().includes('reject') ? (
                            <XCircle className="h-3 w-3 text-red-500" />
                          ) : (
                            <CheckCircle2 className="h-3 w-3 text-slate-400" />
                          )}
                          Reviewed {req.dateReviewed ? formatDateTime(req.dateReviewed) : ''}
                        </span>
                      </div>

                      <div className="p-4">
                        <div className="mb-4 grid grid-cols-2 gap-x-6 gap-y-4">
                          <MetaItem label="Sender" value={req.senderName} avatar />
                          <MetaItem label="Approver" value={req.approverName} avatar />
                          <MetaItem label="Level" value={req.levelName} />
                          <MetaItem label="State" value={req.stateName} />
                        </div>

                        {req.senderRemark && (
                          <RemarkBlock icon={<MessageSquare className="h-3 w-3" />} label="Sender Remark" text={req.senderRemark} />
                        )}
                        {req.reviewRemark && (
                          <RemarkBlock icon={<FileText className="h-3 w-3" />} label="Review Remark" text={req.reviewRemark} />
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>
          )}
        </div>
      )}
    </ActionDrawer>
  )
}
